import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, ContentList, DatePicker, Text } from '@hopskipdrive/react-components';
import { CalendarDropdown } from '@rideiq/shared/components';
import { PageHeading } from '@rideiq/shared/components/PageHeading';

import { DateSectionContainer } from './DateSection.styles';

type formData = {
  anchorTime: Nullable<Date>;
  calendarSlug: Nullable<string>;
};

interface DateSectionForSingleRideProps {
  anchorTime: Nullable<Date>;
  nextAvailableDate: Date;
  isLocationSelected: boolean;
  calendarSlug?: Nullable<string>;
}

export const DateSectionForSingleRide: FC<DateSectionForSingleRideProps> = ({
  anchorTime,
  nextAvailableDate,
  isLocationSelected,
  calendarSlug,
}) => {
  const {
    formState: { errors },
    control,
    setError,
    clearErrors,
  } = useFormContext<formData>();

  return (
    <DateSectionContainer mb={4} id="content-panel">
      <PageHeading variant="h2" fontWeight={500}>
        Single Ride date
      </PageHeading>
      <ContentList>
        <Box display="flex" flexGrow={1} flexWrap="wrap" gap=".5rem" mt={4}>
          <Controller
            control={control}
            name="anchorTime"
            defaultValue={anchorTime}
            render={({ field: { onChange, value } }) => (
              <Box className="core-date-picker-container">
                <DatePicker
                  applyOnBlur
                  onChange={onChange}
                  allowEmpty={false}
                  onError={(error) => {
                    if (error) {
                      setError('anchorTime', { type: 'dateInput', message: 'Please enter a valid date' });
                    } else {
                      clearErrors('anchorTime');
                    }
                  }}
                  minDate={nextAvailableDate}
                  value={value}
                  slotProps={{
                    textField: {
                      error: !!errors.anchorTime,
                      helperText: errors.anchorTime?.message,
                      style: { width: '154px', minWidth: '140px' },
                      inputProps: {
                        id: 'ride-date-picker',
                        'data-testid': 'ride-date-picker',
                      },
                    },
                  }}
                />
              </Box>
            )}
          />
          <Controller
            control={control}
            name="calendarSlug"
            defaultValue={calendarSlug}
            render={({ field: { onChange, value } }) => (
              <Box display="flex" flexGrow={1} alignItems="center">
                {isLocationSelected ? (
                  <CalendarDropdown
                    onChange={onChange}
                    value={value}
                    error={!!errors.calendarSlug}
                    helperText={errors.calendarSlug?.message}
                  />
                ) : (
                  <Text
                    className="calendar-helper-text"
                    variant="body1"
                    textColor="secondaryText"
                    fontWeight={400}
                    data-testid="helper-text"
                  >
                    <i>You can associate this ride with a calendar once the anchor location is selected</i>
                  </Text>
                )}
              </Box>
            )}
          />
        </Box>
      </ContentList>
    </DateSectionContainer>
  );
};
